import { useDataSelectionContext } from "../DataSelectionContext";
import DataSelectionModel from "../DataSelectionModal";
import { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import { Form, Spinner } from "react-bootstrap";
import { WfsGetFeatureRequest } from "../../../types/Wfs/WfsGetFeatureRequest";
import { useLayerService } from "../../../hooks";
import { InrixImport } from "../../../types/Inrix/InrixImport";

export interface InrixVolumeProps {
  boundaryId?: string;
}

interface InrixVolumesFormValues {
  outputFormat: string;
  importId: string;
}

export const InrixVolumes = () => {
  const layerName = "Inrix:Volumes";
  const { layers, getLayer, wfsOrder } = useDataSelectionContext();
  const layer = useMemo(() => getLayer(layerName), [layers]);
  const layerService = useLayerService();
  const [imports, setImports] = useState<InrixImport[]>([]);
  const [loadingImports, setLoadingImports] = useState(true);

  useEffect(() => {
    layerService
      .GetInrixImports()
      .then((data) => setImports(data.filter((i) => i.importType === 0)))
      .catch()
      .finally(() => setLoadingImports(false));
  }, []);

  const initialValues = useMemo(
    () =>
      ({
        outputFormat: "parquet",
        importId: imports.length ? imports[0].id : "",
      } as InrixVolumesFormValues),
    [layer, imports]
  );

  const schema = useMemo(
    () =>
      yup.object({
        outputFormat: yup.string().oneOf(["parquet", "csv"]).required(),
        importId: yup.string().required(),
      }),
    [layer]
  );

  const onSubmit = (values: InrixVolumesFormValues) => {
    wfsOrder({
      service: "WFS",
      version: "2.0.0",
      request: "GetFeature",
      resultType: "Results",
      typeNames: layerName,
      outputFormat: values.outputFormat,
      filter: `InrixImportId = ${values.importId}`,
    } as WfsGetFeatureRequest);
  };

  return (
    <>
      <DataSelectionModel
        headerText="Inrix Volumes"
        initialValues={initialValues}
        validationSchema={schema}
        layer={layer}
        onSubmit={onSubmit}
        enableReinitialize>
        {({ values, handleChange, handleBlur }) => (
          <>
            {loadingImports ? (
              <div className="d-flex align-items-center gap-2 py-2">
                <Spinner size="sm" />
                <span>Loading imports&hellip;</span>
              </div>
            ) : (
              <Form.Group style={{ width: 300 }}>
                <Form.Label htmlFor="importId">Import</Form.Label>
                <Form.Select
                  id="importId"
                  name="importId"
                  value={values.importId}
                  onChange={handleChange}
                  onBlur={handleBlur}>
                  {imports.map((imp) => (
                    <option key={imp.id} value={imp.id}>
                      {imp.label ?? `${imp.id} — ${imp.completedDate.toLocaleString()}`}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            )}
            <Form.Group style={{ width: 300 }}>
              <Form.Label htmlFor="outputFormat">Format</Form.Label>
              <Form.Select
                id="outputFormat"
                name="outputFormat"
                value={values.outputFormat}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={loadingImports}>
                <option value="parquet">Parquet</option>
                <option value="csv">CSV</option>
              </Form.Select>
            </Form.Group>
          </>
        )}
      </DataSelectionModel>
    </>
  );
};
